import { Transaction } from '@mysten/sui/transactions';
import { SUI_SYSTEM_ADDRESS } from '@mysten/sui/utils';
import { appendConsolidateLooseCpcCoins, fetchLooseCpcCoinIds, } from './buildConsolidateCpc.js';
import { CPC_FRAMEWORK, DGC_TYPE } from './constants.js';
/** DGC 白皮书 / `staking_pool::LOCK_PERIOD` */
export const CPC_STAKE_LOCK_EPOCHS = 180;
function prependConsolidateLoose(tx, owner, looseCoinObjectIds) {
    if (looseCoinObjectIds.length > 0) {
        appendConsolidateLooseCpcCoins(tx, owner, looseCoinObjectIds);
    }
}
function claimRewardsFromFungible(tx, fss, recipient) {
    // PTB: non_entry returns Balance; explicit send_funds → AB (entry alone in PTB can leave loose Coin).
    const rewards = tx.moveCall({
        target: `${SUI_SYSTEM_ADDRESS}::sui_system::redeem_fungible_staked_sui_rewards_only_non_entry`,
        arguments: [tx.object.system(), fss],
    });
    tx.moveCall({
        target: `${CPC_FRAMEWORK}::balance::send_funds`,
        typeArguments: [DGC_TYPE],
        arguments: [rewards, tx.pure.address(recipient)],
    });
    tx.transferObjects([fss], tx.pure.address(recipient));
}
/**
 * Claim staking rewards from an active `StakedSui` receipt.
 * P-UX: rewards → address balance via on-chain entry `send_funds`.
 */
export function buildClaimStakeRewardsFromStakedSui({ stakedSuiId, recipient, looseCoinObjectIds = [], }) {
    const tx = new Transaction();
    prependConsolidateLoose(tx, recipient, looseCoinObjectIds);
    const [fss] = tx.moveCall({
        target: `${SUI_SYSTEM_ADDRESS}::sui_system::convert_to_fungible_staked_sui`,
        arguments: [tx.object.system(), tx.object(stakedSuiId)],
    });
    claimRewardsFromFungible(tx, fss, recipient);
    tx.setGasPayment([]);
    return tx;
}
/** Claim rewards from an existing `FungibleStakedSui` (already converted). */
export function buildClaimStakeRewardsFromFungible({ fungibleStakedSuiId, recipient, looseCoinObjectIds = [], }) {
    const tx = new Transaction();
    prependConsolidateLoose(tx, recipient, looseCoinObjectIds);
    const fss = tx.object(fungibleStakedSuiId);
    claimRewardsFromFungible(tx, fss, recipient);
    tx.setGasPayment([]);
    return tx;
}
/** P-UX: merge loose coins first (gas via AB), then claim rewards. */
export async function buildClaimStakeRewardsFromStakedSuiForOwner(client, params) {
    const looseIds = await fetchLooseCpcCoinIds(client, params.recipient);
    return buildClaimStakeRewardsFromStakedSui({ ...params, looseCoinObjectIds: looseIds });
}
export async function buildClaimStakeRewardsFromFungibleForOwner(client, params) {
    const looseIds = await fetchLooseCpcCoinIds(client, params.recipient);
    return buildClaimStakeRewardsFromFungible({ ...params, looseCoinObjectIds: looseIds });
}
/**
 * Redeem principal (+ any unlocked rewards) after `lock_until_epoch`.
 * Requires `FungibleStakedSui`; converts from `StakedSui` first if needed (use claim flow).
 */
export function buildRedeemStakePrincipal({ fungibleStakedSuiId, recipient, looseCoinObjectIds = [], }) {
    const tx = new Transaction();
    prependConsolidateLoose(tx, recipient, looseCoinObjectIds);
    const withdrawn = tx.moveCall({
        target: `${SUI_SYSTEM_ADDRESS}::sui_system::redeem_fungible_staked_sui`,
        arguments: [tx.object.system(), tx.object(fungibleStakedSuiId)],
    });
    tx.moveCall({
        target: `${CPC_FRAMEWORK}::balance::send_funds`,
        typeArguments: [DGC_TYPE],
        arguments: [withdrawn, tx.pure.address(recipient)],
    });
    tx.setGasPayment([]);
    return tx;
}
/** Convert `StakedSui` → `FungibleStakedSui` then redeem principal (fails if still locked). */
export function buildRedeemStakePrincipalFromStakedSui({ stakedSuiId, recipient, looseCoinObjectIds = [], }) {
    const tx = new Transaction();
    prependConsolidateLoose(tx, recipient, looseCoinObjectIds);
    const [fss] = tx.moveCall({
        target: `${SUI_SYSTEM_ADDRESS}::sui_system::convert_to_fungible_staked_sui`,
        arguments: [tx.object.system(), tx.object(stakedSuiId)],
    });
    const withdrawn = tx.moveCall({
        target: `${SUI_SYSTEM_ADDRESS}::sui_system::redeem_fungible_staked_sui`,
        arguments: [tx.object.system(), fss],
    });
    tx.moveCall({
        target: `${CPC_FRAMEWORK}::balance::send_funds`,
        typeArguments: [DGC_TYPE],
        arguments: [withdrawn, tx.pure.address(recipient)],
    });
    tx.setGasPayment([]);
    return tx;
}
export async function buildRedeemStakePrincipalForOwner(client, params) {
    const looseIds = await fetchLooseCpcCoinIds(client, params.recipient);
    return buildRedeemStakePrincipal({ ...params, looseCoinObjectIds: looseIds });
}
export async function buildRedeemStakePrincipalFromStakedSuiForOwner(client, params) {
    const looseIds = await fetchLooseCpcCoinIds(client, params.recipient);
    return buildRedeemStakePrincipalFromStakedSui({ ...params, looseCoinObjectIds: looseIds });
}
